import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import MovieCard from '@/components/feature/MovieCard';
import { useFavorites, formatDate } from '@/hooks/useFavorites';
import { getMovieById, getRelatedMovies } from '@/utils/movie';
import { typeLabels } from '@/mocks/movies';

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const movie = getMovieById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState('');

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(''), 2200);
  };

  if (!movie) {
    return (
      <SiteLayout title="作品不存在 - 丝瓜视频" description="未找到对应的影视作品，可能已下架或链接有误。">
        <main className="flex-1 w-full px-4 md:px-8 py-24 flex flex-col items-center text-center">
          <span className="w-16 h-16 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center mb-5">
            <i className="ri-film-line text-3xl"></i>
          </span>
          <h1 className="font-heading text-2xl font-black text-foreground-950">没有找到这部作品</h1>
          <p className="mt-2 text-sm text-foreground-600 max-w-md">
            链接可能已失效，或者该作品已被移除。你可以回到首页看看正在更新的内容。
          </p>
          <Link
            to="/"
            className="mt-6 px-5 h-11 inline-flex items-center gap-2 rounded-md bg-primary-500 text-background-50 text-sm font-bold whitespace-nowrap"
          >
            <i className="ri-home-4-line"></i> 返回首页
          </Link>
        </main>
      </SiteLayout>
    );
  }

  const related = getRelatedMovies(movie, 6);
  const favored = isFavorite(movie.id);

  const handleFavorite = () => {
    const added = toggleFavorite(movie.id);
    showToast(added ? '已加入我的收藏' : '已从收藏中移除');
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        showToast('链接已复制（演示分享）');
      } else {
        showToast('分享功能为前端演示，请手动复制地址栏链接');
      }
    } catch {
      showToast('分享功能为前端演示，请手动复制地址栏链接');
    }
  };

  return (
    <SiteLayout
      title={`${movie.title} - 丝瓜视频`}
      description={`${movie.title}（${movie.year}，${typeLabels[movie.type]}）：${movie.summary.slice(0, 70)}`}
      keywords={`${movie.title},${typeLabels[movie.type]},${movie.genres.join(',')},丝瓜视频`}
    >
      <main className="flex-1 w-full pb-10">
        <section className="relative w-full min-h-[420px] md:min-h-[480px] overflow-hidden bg-foreground-950">
          <img
            src={movie.backdrop}
            alt={`${movie.title} 剧照`}
            className="absolute inset-0 w-full h-full object-cover object-top opacity-70"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/20"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20"></div>

          <div className="relative px-4 md:px-8 py-6 md:py-10">
            <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-white/65 mb-5 flex-wrap">
              <Link to="/" className="hover:text-white transition-colors">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to={`/category/${movie.type}`} className="hover:text-white transition-colors">
                {typeLabels[movie.type]}
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-white truncate max-w-[240px]">{movie.title}</span>
            </nav>

            <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-start">
              <div className="w-32 sm:w-40 md:w-52 shrink-0 rounded-lg overflow-hidden border border-white/15 bg-background-900">
                <div className="w-full aspect-[5/7]">
                  <img
                    src={movie.poster}
                    alt={`${movie.title} 海报`}
                    title={`${movie.title} - ${typeLabels[movie.type]}`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0 text-white">
                <div className="flex items-center gap-2 flex-wrap mb-3">
                  <Link
                    to={`/category/${movie.type}`}
                    className="px-2.5 py-1 rounded bg-primary-500 text-background-50 text-xs font-bold"
                  >
                    {typeLabels[movie.type]}
                  </Link>
                  <span className="px-2.5 py-1 rounded bg-white/15 text-xs">{movie.status}</span>
                  <span className="px-2.5 py-1 rounded bg-white/10 text-xs">{movie.episodes}</span>
                </div>

                <h1 className="font-heading text-2xl md:text-4xl font-black tracking-tight">
                  {movie.title}
                </h1>

                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs md:text-sm text-white/75">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-star-fill text-accent-500"></i>
                    <span className="font-label font-bold text-white text-base">
                      {movie.rating.toFixed(1)}
                    </span>
                    / 10
                  </span>
                  <span>{movie.year}</span>
                  <span>{movie.region}</span>
                  <span>更新于 {formatDate(movie.updatedAt)}</span>
                </div>

                <div className="mt-4 space-y-1.5 text-xs md:text-sm text-white/75">
                  <p>导演：<span className="text-white">{movie.director}</span></p>
                  <p>主演：<span className="text-white">{movie.cast.join(' / ')}</span></p>
                  <div className="flex items-center gap-1.5 flex-wrap pt-1">
                    {movie.genres.map((genre) => (
                      <span
                        key={genre}
                        className="px-2 py-0.5 rounded bg-white/10 border border-white/20 text-[11px]"
                      >
                        {genre}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={handleFavorite}
                    className={`px-5 h-11 inline-flex items-center gap-2 rounded-md text-sm font-bold transition-colors whitespace-nowrap cursor-pointer ${
                      favored
                        ? 'bg-white/15 border border-white/40 text-white'
                        : 'bg-primary-500 text-background-50 hover:bg-primary-600'
                    }`}
                  >
                    <i className={`${favored ? 'ri-bookmark-fill' : 'ri-bookmark-3-line'} text-base`}></i>
                    {favored ? '已收藏' : '收藏这部作品'}
                  </button>
                  <button
                    type="button"
                    onClick={handleShare}
                    className="px-5 h-11 inline-flex items-center gap-2 rounded-md bg-white/12 border border-white/30 text-white text-sm font-medium hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-share-forward-line text-base"></i>
                    分享
                  </button>
                  <span className="text-[11px] text-white/50">收藏保存在本机浏览器 · 分享为前端演示</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 mt-8">
          <div className="flex flex-col lg:flex-row gap-5 lg:gap-6">
            <div className="flex-1 min-w-0 space-y-5">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-1.5 h-5 rounded-full bg-primary-500"></span>
                  <h2 className="font-heading text-lg font-bold text-foreground-950">剧情简介</h2>
                </div>
                <p className="text-sm text-foreground-700 leading-7">{movie.summary}</p>
                <p className="mt-4 text-[11px] text-foreground-400 leading-relaxed">
                  说明：本页面仅展示作品信息，不提供真实播放源；作品、主创与剧情均为虚构演示内容。
                </p>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="w-1.5 h-5 rounded-full bg-accent-500"></span>
                  <h2 className="font-heading text-lg font-bold text-foreground-950">作品信息</h2>
                </div>
                <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-4 text-sm">
                  {[
                    { label: '作品类型', value: typeLabels[movie.type] },
                    { label: '上映年份', value: `${movie.year} 年` },
                    { label: '制作地区', value: movie.region },
                    { label: '集数 / 时长', value: movie.episodes },
                    { label: '更新状态', value: movie.status },
                    { label: '内容标签', value: movie.genres.join(' / ') },
                    { label: '导演', value: movie.director },
                    { label: '主演', value: movie.cast.join(' / ') },
                    { label: '热度指数', value: `${movie.popularity} / 100` },
                  ].map((row) => (
                    <div key={row.label}>
                      <dt className="text-xs text-foreground-500">{row.label}</dt>
                      <dd className="mt-1 text-sm text-foreground-900 font-medium">{row.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>

            <aside className="w-full lg:w-[280px] shrink-0 space-y-4">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h3 className="font-heading text-sm font-bold text-foreground-950 mb-3">所属分类</h3>
                <div className="space-y-2">
                  {(['movie', 'tv', 'variety'] as const).map((item) => (
                    <Link
                      key={item}
                      to={`/category/${item}`}
                      className={`flex items-center justify-between px-3 py-2.5 rounded-md text-sm transition-colors ${
                        item === movie.type
                          ? 'bg-primary-500 text-background-50 font-medium'
                          : 'bg-background-50 text-foreground-700 hover:text-primary-600 border border-background-200'
                      }`}
                    >
                      {typeLabels[item]}频道
                      <i className="ri-arrow-right-s-line"></i>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h3 className="font-heading text-sm font-bold text-foreground-950 mb-3">同类精选</h3>
                <div className="space-y-3">
                  {related.slice(0, 3).map((item) => (
                    <Link key={item.id} to={`/detail/${item.id}`} className="flex gap-3 group">
                      <div className="w-12 aspect-[5/7] rounded overflow-hidden bg-background-200 shrink-0">
                        <img
                          src={item.poster}
                          alt={item.title}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground-900 truncate group-hover:text-primary-600 transition-colors">
                          {item.title}
                        </p>
                        <p className="mt-1 text-[11px] text-foreground-500">
                          {item.year} · {item.region}
                        </p>
                        <p className="text-[11px] text-primary-600 font-label">{item.rating.toFixed(1)} 分</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 mt-10 md:mt-14">
          <div className="flex items-end justify-between gap-4 mb-5">
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-5 rounded-full bg-primary-500"></span>
              <h2 className="font-heading text-xl md:text-2xl font-black text-foreground-950">相关推荐</h2>
            </div>
            <Link
              to={`/category/${movie.type}`}
              className="text-xs md:text-sm text-foreground-600 hover:text-primary-600 flex items-center gap-1 whitespace-nowrap"
            >
              更多{typeLabels[movie.type]}
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-5">
            {related.map((item) => (
              <MovieCard key={item.id} movie={item} />
            ))}
          </div>
        </section>
      </main>

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[70] px-5 py-3 rounded-md bg-foreground-950 text-background-50 text-sm animate-fade-up whitespace-nowrap">
          {toast}
        </div>
      )}
    </SiteLayout>
  );
}